import { useEffect, useState } from 'react';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames, formatDateTime } from '@/lib/utils';
import type { Lead } from '@/lib/types';
import type { ProcesoConversacion, PropuestaConversacion } from '@/lib/comunicaciones';
import { CAMPOS, TEMPERATURA, comoSeLee } from '@/components/llamadas/ProcesarLead';

/**
 * «Procesar conversación» de WhatsApp: la IA lee los mensajes nuevos desde el
 * último proceso y propone lo que apuntar en la Actividad del lead, con la
 * misma lectura que «Procesar para Lead» de las llamadas.
 *
 * Al contrario que en las llamadas, aquí nada se apunta solo: se enseña la
 * propuesta, se puede retocar (el texto, qué datos de la ficha rellenar, si se
 * mueve de fase) y se apunta al aceptar.
 */
export default function ProcesarConversacion({ lead, onCancelar, onAceptada }: {
  lead: Lead;
  onCancelar: () => void;
  onAceptada: () => void;
}) {
  const [proceso, setProceso] = useState<ProcesoConversacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState('');
  const [puntos, setPuntos] = useState('');
  const [objeciones, setObjeciones] = useState('');
  const [siguiente, setSiguiente] = useState('');
  const [temperatura, setTemperatura] = useState<PropuestaConversacion['temperatura']>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [moverAFase, setMoverAFase] = useState(false);
  const [aceptando, setAceptando] = useState(false);

  useEffect(() => {
    let vigente = true;
    api.procesarConversacionLead(lead.id)
      .then(p => {
        if (!vigente) return;
        setProceso(p);
        setResumen(p.propuesta.resumen);
        setPuntos(p.propuesta.puntosClave.join('\n'));
        setObjeciones(p.propuesta.objeciones.join('\n'));
        setSiguiente(p.propuesta.siguientePaso || '');
        setTemperatura(p.propuesta.temperatura);
        setMarcados(new Set(Object.keys(p.propuesta.campos)));
      })
      .catch(err => { if (vigente) setError(err instanceof Error ? err.message : 'No se pudo leer la conversación'); });
    return () => { vigente = false; };
  }, [lead.id]);

  if (error) {
    return (
      <div className="flex-1 p-5 space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" onClick={onCancelar} className="btn-ghost border border-gray-200 text-sm">Volver al chat</button>
      </div>
    );
  }
  if (!proceso) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-sm text-sereno-dark/60 px-6 text-center">
        <Sparkles size={20} className="text-sereno-dark/40 animate-pulse" />
        Leyendo la conversación…
        <span className="text-xs text-sereno-dark/40">Puede tardar un minuto.</span>
      </div>
    );
  }

  const p = proceso.propuesta;
  const lineas = (t: string) => t.split('\n').map(x => x.trim()).filter(Boolean);
  const fase = p.faseSugerida && p.faseSugerida.id !== proceso.faseActual ? proceso.fases.find(f => f.id === p.faseSugerida!.id) : null;
  const campos = Object.entries(p.campos);

  const aceptar = async () => {
    setAceptando(true);
    setError(null);
    try {
      await api.aceptarConversacionLead(lead.id, {
        desde: proceso.desde,
        hasta: proceso.hasta,
        mensajes: proceso.mensajes,
        moverAFase: !!fase && moverAFase,
        propuesta: {
          ...p,
          resumen: resumen.trim(),
          puntosClave: lineas(puntos),
          objeciones: lineas(objeciones),
          siguientePaso: siguiente.trim() || null,
          temperatura,
          campos: Object.fromEntries(campos.filter(([k]) => marcados.has(k))),
        },
      });
      onAceptada();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo apuntar');
      setAceptando(false);
    }
  };

  return (
    <>
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="text-xs text-sereno-dark/50 flex items-start gap-1.5">
          <Sparkles size={13} className="mt-0.5 shrink-0" />
          <span>
            Propuesta para la Actividad del lead, de {proceso.mensajes} {proceso.mensajes === 1 ? 'mensaje' : 'mensajes'} ({formatDateTime(proceso.desde)}
            {proceso.desde !== proceso.hasta && <> – {formatDateTime(proceso.hasta)}</>}). Retócala antes de apuntarla: no se guarda nada hasta que aceptes.
          </span>
        </div>

        <div>
          <label className="label">Resumen</label>
          <textarea className="input text-sm" rows={4} value={resumen} onChange={e => setResumen(e.target.value)} />
        </div>
        <div>
          <label className="label">Puntos clave <span className="font-normal text-sereno-dark/35">· uno por línea</span></label>
          <textarea className="input text-sm" rows={3} value={puntos} onChange={e => setPuntos(e.target.value)} placeholder="Nada que destacar" />
        </div>
        <div>
          <label className="label">Objeciones <span className="font-normal text-sereno-dark/35">· una por línea</span></label>
          <textarea className="input text-sm" rows={2} value={objeciones} onChange={e => setObjeciones(e.target.value)} placeholder="No salió ninguna" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px] gap-3">
          <div>
            <label className="label">Siguiente paso</label>
            <input className="input text-sm" value={siguiente} onChange={e => setSiguiente(e.target.value)} placeholder="Nada acordado" />
          </div>
          <div>
            <label className="label">Temperatura</label>
            <select className={classNames('input text-sm', temperatura && TEMPERATURA[temperatura]?.clase)} value={temperatura || ''}
              onChange={e => setTemperatura((e.target.value || null) as PropuestaConversacion['temperatura'])}>
              <option value="">—</option>
              {Object.entries(TEMPERATURA).map(([k, t]) => <option key={k} value={k}>{t.texto}</option>)}
            </select>
          </div>
        </div>

        {campos.length > 0 && (
          <div>
            <label className="label">Datos para la ficha <span className="font-normal text-sereno-dark/35">· solo los que no tiene</span></label>
            <div className="space-y-1.5">
              {campos.map(([k, v]) => (
                <label key={k} className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={marcados.has(k)}
                    onChange={e => setMarcados(m => { const n = new Set(m); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />
                  <span><span className="text-sereno-dark/50">{CAMPOS[k] || k}:</span> {comoSeLee(k, v)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {fase && (
          <label className="flex items-start gap-2 text-sm rounded-lg border border-gray-200 p-3">
            <input type="checkbox" className="mt-1" checked={moverAFase} onChange={e => setMoverAFase(e.target.checked)} />
            <span>
              <span className="flex items-center gap-1 font-medium text-sereno-dark">Mover a la fase <ArrowRight size={13} /> {fase.nombre}</span>
              {p.faseSugerida?.porQue && <span className="block text-xs text-sereno-dark/50 mt-0.5">{p.faseSugerida.porQue}</span>}
            </span>
          </label>
        )}
      </div>

      <div className="border-t border-gray-100 p-3 space-y-2" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        {error && <div className="text-xs text-red-600">{error}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onCancelar} disabled={aceptando} className="btn-ghost border border-gray-200 text-sm flex-1">Descartar</button>
          <button type="button" onClick={() => void aceptar()} disabled={aceptando || !resumen.trim()} className="btn-primary text-sm flex-[2] flex items-center justify-center gap-2">
            {aceptando ? <Loader2 size={15} className="animate-spin" /> : null} Apuntar en la actividad
          </button>
        </div>
      </div>
    </>
  );
}
